import { ReactNode } from 'react';

import { AxiosResponse, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';
import { FORM_ERROR } from 'final-form';

import { IErrorMessages } from '../../molecules/Form/interfaces';
import { X_TOTAL_COUNT } from '../../store/redux-api/constants';
import fetchErrorFromResponse from '../fetchErrorFromResponse';

type IAnyObject = Record<string, unknown>;

interface IResponse<T = Record<string, unknown>> {
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
  response: T;
}

export type OnError<T> = (
  /** Error message as a string. */
  error: string,

  /** HTTP status of the request (if available). */
  status: number | undefined,

  /** Response for the API call (if available). */
  response: AxiosResponse<T> | undefined,

  /** Error thrown by request. */
  errorObject: unknown
) => ReactNode | void;
export type OnSuccess<T> = (data: T, headers: AxiosResponse<T>['headers']) => void;

export async function formRequest<T = IAnyObject, R = IAnyObject>(
  request: () => Promise<AxiosResponse<R>>,
  onSuccess?: OnSuccess<R>,
  onError?: OnError<R>
): Promise<boolean | IErrorMessages<T>> {
  const errors: IErrorMessages<T> = {};
  let customError: string = null;

  try {
    const { data, headers } = await request();
    if (onSuccess) {
      onSuccess(data, headers);
    }
    return true;
  } catch (error) {
    const errorMessage = fetchErrorFromResponse(error?.response?.data);
    const responseStatus = error?.response?.status;

    if (onError) {
      customError = onError(errorMessage, responseStatus, error?.response, error) as
        | string
        | null
        | undefined;
    }
    errors[FORM_ERROR] = customError ?? errorMessage;
    return errors;
  }
}

type IHeaders = RawAxiosResponseHeaders | AxiosResponseHeaders;
export const getTotalFromHeader = (headers: IHeaders | Headers): number => {
  if (headers) {
    if (typeof headers.get === 'function') {
      return Number((headers as Headers).get(X_TOTAL_COUNT) ?? 0);
    }
    return Number(
      ((headers as IHeaders)[X_TOTAL_COUNT.toLowerCase()] ||
        (headers as IHeaders)[X_TOTAL_COUNT]) ??
        0
    );
  }
  return 0;
};

/**
 * @deprecated useAxiosQuery instead of formatting response additionally
 * @param data
 * @param headers
 */
export const formatResponse = <T = Record<string, unknown>>({
  data,
  headers,
}: AxiosResponse<T>): IResponse<T> => ({
  response: data,
  headers,
});
